import { useAppStore } from '../../store/appStore';
import { useLiveJobStore } from '../../store/liveJobStore';
import { useMcpJobManager } from './useMcpJobManager';
import { useMcpNotificationCenter, useMcpErrorDeduplication, type McpNotification } from './useMcpNotificationCenter';
import { useMcpJobAnalysis, useMcpJobAnalysisFilter } from './useMcpJobAnalysis';
import JobDetailModal from './JobDetailModal';
import JobMetricsPanel from '../chat/components/JobMetricsPanel';
import LiveEventsViewer from './LiveEventsViewer';
import ParaLexTraceChat from './ParaLexTraceChat';
import McpStatusPanel from './McpStatusPanel';

import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';

// Document processing job types for visual identification
const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
  'TextExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

const isDocumentJob = (jobName?: string) => {
  return jobName && DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
};

export default function JobMonitorPage() {
  const { keycloak } = useKeycloak();
  const { currentFirmId } = useAppStore();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'waiting' | 'failed' | 'completed' | 'document' | 'ai-analyzed'>('all');
  const [isParaLexChatOpen, setIsParaLexChatOpen] = useState(false);
  
  // 🚀 OPTIMIZED: Centralized MCP notification system
  const { 
    notifications: mcpNotifications, 
    pushNotification, 
    dismissNotification 
  } = useMcpNotificationCenter();
  
  const { shouldPushError } = useMcpErrorDeduplication();
  
  // 🚀 NEW: Multi-realm support for developers/admins
  const [selectedRealmId, setSelectedRealmId] = useState<string | null>(currentFirmId);
  const [adminMode, setAdminMode] = useState(false);
  const [availableRealms, setAvailableRealms] = useState<string[]>([]);

  // Use ONLY the persistent liveJobStore (removes duplicate monitoring)
  const {
    connected,
    jobsById,
    agentActivity,
    recentEvents,
    metrics,
    documentMetrics,
    lastUpdated,
    error,
    initializeSocket,
    loadExistingJobs,
    clearError
  } = useLiveJobStore();

  // 🚀 NEW: MCP Job Manager Integration
  const {
    state: mcpState,
    operations: mcpOperations,
    canUseMcp: mcpHealthy
  } = useMcpJobManager();

  // 🚀 NEW: MCP Job Analysis Integration
  const {
    isAnalyzing,
    isBatchAnalyzing,
    error: mcpAnalysisError,
    analyzeFailedJob,
    analyzeFailedJobs,
    refreshAnalysisData,
    hasAnalysis,
    getAnalysisIndicator,
    // 🚀 NEW: Enhanced accessors (Phase 2)
    getAnalysisStatus,
    getAnalysisSummary,
    getLastAnalyzedAt,
    isAnalysisStale
  } = useMcpJobAnalysis();

  // 🚀 NEW: MCP Job Analysis Filter Helper
  const { 
    getAnalysisCount
  } = useMcpJobAnalysisFilter();

  // Initialize persistent store when component mounts or realm changes
  useEffect(() => {
    if (keycloak?.token) {
      const realmToUse = adminMode ? selectedRealmId : (selectedRealmId || currentFirmId);
      console.log(`[JobMonitorPage] Initializing for realm: ${realmToUse || 'ALL'} (admin: ${adminMode})`);
      initializeSocket(keycloak.token, realmToUse, adminMode);
      loadExistingJobs(keycloak.token, realmToUse, adminMode);
    }
  }, [keycloak?.token, selectedRealmId, adminMode, currentFirmId, initializeSocket, loadExistingJobs]);

  // 🚀 OPTIMIZED: Monitor MCP errors with deduplication
  useEffect(() => {
    if (mcpState.error && shouldPushError(mcpState.error)) {
      pushNotification(mcpState.error, 'error', false); // Don't auto-hide errors
    }
  }, [mcpState.error, shouldPushError, pushNotification]);

  // 🚀 NEW: Monitor MCP analysis errors
  useEffect(() => {
    if (mcpAnalysisError && shouldPushError(mcpAnalysisError)) {
      pushNotification(`Analysis Error: ${mcpAnalysisError}`, 'error', false);
    }
  }, [mcpAnalysisError, shouldPushError, pushNotification]);

  // 🚀 NEW: Sync MCP analysis data when jobs are updated
  useEffect(() => {
    if (mcpHealthy && Object.keys(jobsById).length > 0) {
      const failedJobIds = Object.values(jobsById)
        .filter(job => job.status === 'failed')
        .map(job => job.id);
      
      if (failedJobIds.length > 0) {
        // Refresh analysis data for failed jobs (non-blocking)
        refreshAnalysisData(failedJobIds).catch(error => {
          console.warn('Failed to refresh MCP analysis data:', error);
        });
      }
    }
    // Note: We intentionally depend on lastUpdated rather than jobsById to avoid
    // excessive re-renders. lastUpdated changes when jobs are modified.
  }, [lastUpdated, mcpHealthy, refreshAnalysisData, jobsById]); // Trigger when jobs are updated

  // Extract available realms from events and jobs
  useEffect(() => {
    const realmsFromEvents = recentEvents
      .map(event => event.realmId)
      .filter((realm): realm is string => Boolean(realm));
    
    const realmsFromJobs = Object.values(jobsById)
      .map(job => job.data?._meta?.realmId || job.data?.realmId)
      .filter((realm): realm is string => Boolean(realm));
    
    const allRealms = [...new Set([...realmsFromEvents, ...realmsFromJobs].filter(Boolean) as string[])];
    if (currentFirmId) allRealms.push(currentFirmId);
    setAvailableRealms([...new Set(allRealms)]);
  }, [recentEvents, jobsById, currentFirmId]);

  // Convert jobsById object to array for filtering
  const jobs = Object.values(jobsById);
  const selectedJob = selectedJobId ? jobsById[selectedJobId] : null;

  const openJobModal = (jobId: string) => {
    setSelectedJobId(jobId);
  };

  const closeJobModal = () => {
    setSelectedJobId(null);
  };

  // 🚀 Enhanced refresh with MCP data
  const handleRefresh = async () => {
    if (keycloak?.token) {
      const realmToUse = adminMode ? selectedRealmId : (selectedRealmId || currentFirmId);
      loadExistingJobs(keycloak.token, realmToUse, adminMode);
      
      // 🚀 NEW: Also refresh MCP analysis data if healthy
      if (mcpHealthy) {
        try {
          const allJobIds = Object.keys(jobsById);
          if (allJobIds.length > 0) {
            await refreshAnalysisData(allJobIds);
            pushNotification('Refreshed job data and MCP analysis', 'success');
          }
        } catch (error) {
          console.warn('Failed to refresh MCP analysis data during refresh:', error);
          pushNotification('Refreshed job data (MCP analysis refresh failed)', 'warning');
        }
      } else {
        pushNotification('Refreshed job data', 'success');
      }
    }
  };

  // Filter jobs based on current filter and realm (if admin mode)
  const filteredJobs = jobs.filter(job => {
    // Realm filtering (when in admin mode and specific realm selected)
    if (adminMode && selectedRealmId) {
      const jobRealmId = job.data?._meta?.realmId || job.data?.realmId;
      if (jobRealmId !== selectedRealmId) return false;
    }

    // Status filtering
    switch (filter) {
      case 'active':
        return job.status === 'active';
      case 'waiting':
        return job.status === 'waiting';
      case 'failed':
        return job.status === 'failed';
      case 'completed':
        return job.status === 'completed';
      case 'document':
        return isDocumentJob(job.name);
      case 'ai-analyzed':
        // 🚀 NEW: Use real MCP analysis data
        return hasAnalysis(job.id);
      default:
        return true;
    }
  });

  const getJobIcon = (jobName: string) => {
    if (isDocumentJob(jobName)) return '📄';
    return '⚙️';
  };

  // 🚀 NEW: Get MCP analysis indicator for job using real data
  const getMcpAnalysisIndicator = (jobId: string) => {
    return getAnalysisIndicator(jobId);
  };

  // 🚀 ENHANCED: Enhanced job count with real MCP analysis data + staleness tracking (Phase 2)
  const getFilterCounts = () => {
    const allJobs = Object.values(jobsById);
    const analyzedCount = getAnalysisCount();
    const staleAnalysisCount = allJobs.filter(job => 
      hasAnalysis(job.id) && isAnalysisStale(job.id)
    ).length;
    
    return {
      all: allJobs.length,
      active: allJobs.filter(j => j.status === 'active').length,
      waiting: allJobs.filter(j => j.status === 'waiting').length,
      failed: allJobs.filter(j => j.status === 'failed').length,
      completed: allJobs.filter(j => j.status === 'completed').length,
      document: allJobs.filter(j => isDocumentJob(j.name)).length,
      'ai-analyzed': analyzedCount,
      // 🚀 NEW: Stale analysis tracking
      staleAnalysis: staleAnalysisCount
    };
  };

  const getStatusBadge = (status: string) => {
    const baseClass = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status.toLowerCase()) {
      case 'active':
        return `${baseClass} bg-lex-accent-soft text-lex-accent`;
      case 'waiting':
        return `${baseClass} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'completed':
        return `${baseClass} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'failed':
        return `${baseClass} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200`;
    }
  };

  const filterOptions: { value: typeof filter; label: string; icon: string }[] = [
    { value: 'active', label: 'Active', icon: '🔄' },
    { value: 'waiting', label: 'Waiting', icon: '⏳' },
    { value: 'failed', label: 'Failed', icon: '❌' },
    { value: 'completed', label: 'Completed', icon: '✅' },
    { value: 'document', label: 'Document Jobs', icon: '📄' },
    { value: 'ai-analyzed', label: 'AI Analyzed', icon: '🧠' },
    { value: 'all', label: 'All Jobs', icon: '📊' }
  ];

  const filterCounts = getFilterCounts();

  return (
    <div className="p-6 dark:bg-gray-900 min-h-screen transition-colors">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">EdgeLex Job Monitor</h1>
            <div className="mt-2 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <p className="text-gray-600 dark:text-gray-300">
                  Viewing jobs for realm: 
                  <strong className="ml-1">
                    {adminMode && !selectedRealmId ? 'ALL REALMS' : (selectedRealmId || currentFirmId)}
                  </strong>
                </p>
                {adminMode && (
                  <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 rounded text-xs font-medium">
                    ADMIN MODE
                  </span>
                )}
              </div>
              
              {/* 🚀 NEW: Admin Mode Toggle & Realm Selector */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={adminMode}
                    onChange={(e) => {
                      setAdminMode(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedRealmId(currentFirmId);
                      }
                    }}
                    className="rounded"
                  />
                  <span className="text-gray-600 dark:text-gray-300">Admin Mode (All Realms)</span>
                </label>
                
                {adminMode && availableRealms.length > 0 && (
                  <select
                    value={selectedRealmId || ''}
                    onChange={(e) => setSelectedRealmId(e.target.value || null)}
                    className="text-black dark:text-white bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm"
                  >
                    <option value="">All Realms</option>
                    {availableRealms.map((realm) => (
                      <option key={realm} value={realm}>
                        {realm}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
          {/* 🚀 NEW: Real-time connection status */}
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
              connected 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              {connected ? 'Live Connected' : 'Disconnected'}
            </div>
            {lastUpdated && (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
        
        {(error || mcpState.error) && (
          <div className="mt-2 space-y-2">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    <span className="font-semibold">Job Monitor Error:</span> {error}
                  </p>
                  <button
                    onClick={clearError}
                    className="text-red-600 hover:text-red-800 text-sm underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            {mcpState.error && (
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-orange-800 dark:text-orange-200">
                    <span className="font-semibold">MCP Service:</span> {mcpState.error}
                  </p>
                  <button
                    onClick={mcpOperations.clearError}
                    className="text-orange-600 hover:text-orange-800 text-sm underline"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🚀 OPTIMIZED: Centralized MCP Analysis Notifications */}
        {mcpNotifications.length > 0 && (
          <div className="mt-2 space-y-2">
            {mcpNotifications.map((notification: McpNotification) => (
              <div key={notification.id} className={`p-3 rounded-lg border flex items-center justify-between ${
                notification.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' :
                notification.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' :
                'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }`}>
                <p className={`text-sm ${
                  notification.type === 'success' ? 'text-green-800 dark:text-green-200' :
                  notification.type === 'warning' ? 'text-yellow-800 dark:text-yellow-200' :
                  'text-red-800 dark:text-red-200'
                }`}>
                  <span className="font-semibold">🧠 MCP Analysis:</span> {notification.message}
                </p>
                <button
                  onClick={() => dismissNotification(notification.id)}
                  className={`text-sm underline ${
                    notification.type === 'success' ? 'text-green-600 hover:text-green-800' :
                    notification.type === 'warning' ? 'text-yellow-600 hover:text-yellow-800' :
                    'text-red-600 hover:text-red-800'
                  }`}
                >
                  Dismiss
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System Monitor Navigation */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-1">System Monitor Tools</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Advanced monitoring and analysis tools</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/system-monitor/ws-log"
              className="flex items-center gap-2 px-4 py-2 bg-lex-accent-soft text-lex-accent rounded-lg hover:bg-lex-state-hover transition-colors"
            >
              <span>📡</span>
              <span className="font-medium">WebSocket Logs</span>
            </Link>
            <Link
              to="/system-monitor/schema-validation"
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/40 transition-colors"
            >
              <span>📋</span>
              <span className="font-medium">Schema Validation</span>
            </Link>
          </div>
        </div>
      </div>

      {/* 🚀 Enhanced MCP Status Panel - Replaces Static AI Status */}
      {mcpHealthy ? (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-lex-accent-soft dark:from-purple-900/20 dark:to-lex-accent-soft rounded-lg border border-purple-200 dark:border-purple-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
                <span className="text-lg">🤖</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-purple-900 dark:text-purple-200">ParaLex MCP Orchestration</h3>
                <p className="text-sm text-purple-700 dark:text-purple-300">Multi-AI system powered job analysis and DevOps intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-purple-600 dark:text-purple-400">
                  <span className="font-medium">{metrics.failed || 0}</span> Failed Jobs
                </div>
                <div className="text-xs text-purple-500 dark:text-purple-400">
                  {(metrics.failed || 0) > 0 ? 'Analysis Available' : 'MCP Ready'}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsParaLexChatOpen(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  💬 Chat with ParaLex
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">MCP Active</span>
                </div>
              </div>
            </div>
          </div>
          
          {(metrics.failed || 0) > 0 && (
            <div className="mt-3 p-3 bg-purple-100 dark:bg-purple-900/30 rounded border border-purple-200 dark:border-purple-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-purple-800 dark:text-purple-200">
                  <span className="font-medium">⚠️ Failed jobs detected!</span> MCP orchestration can analyze failures across multiple AI providers.
                </div>
                <button
                  onClick={async () => {
                    const failedJobs = Object.values(jobsById).filter(job => job.status === 'failed');
                    const unanalyzedFailed = failedJobs.filter(job => !hasAnalysis(job.id));
                    const staleFailed = failedJobs.filter(job => hasAnalysis(job.id) && isAnalysisStale(job.id));
                    
                    console.log(`[MCP] Analyzing ${failedJobs.length} failed jobs (${unanalyzedFailed.length} new, ${staleFailed.length} stale)`);
                    
                    // 🚀 ENHANCED: More detailed user feedback (Phase 2)
                    if (failedJobs.length > 0) {
                      try {
                        const contextInfo = unanalyzedFailed.length > 0 && staleFailed.length > 0 
                          ? ` (${unanalyzedFailed.length} new, ${staleFailed.length} stale)`
                          : unanalyzedFailed.length > 0 
                            ? ` (${unanalyzedFailed.length} new)`
                            : staleFailed.length > 0 
                              ? ` (${staleFailed.length} stale)`
                              : '';
                        
                        pushNotification(
                          `Started analysis of ${failedJobs.length} failed job${failedJobs.length > 1 ? 's' : ''}${contextInfo}`,
                          'success'
                        );
                        
                        // 🚀 NEW: Real MCP batch analysis implementation
                        const jobIds = failedJobs.map(job => job.id);
                        await analyzeFailedJobs(jobIds, {
                          analysisType: 'comprehensive',
                          mcpStrategy: 'auto'
                        });
                        
                        pushNotification(
                          `Completed analysis of ${failedJobs.length} failed job${failedJobs.length > 1 ? 's' : ''}${contextInfo}`,
                          'success'
                        );
                      } catch (error) {
                        console.error('Batch analysis failed:', error);
                        pushNotification(
                          `Batch analysis failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                          'error',
                          false
                        );
                      }
                    } else {
                      pushNotification('No failed jobs to analyze', 'warning');
                    }
                  }}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  disabled={mcpState.isLoading || isBatchAnalyzing}
                >
                  {isBatchAnalyzing ? (
                    <>⏳ Analyzing...</>
                  ) : (
                    <>
                      🔍 Analyze Failures
                      {(() => {
                        const failedJobs = Object.values(jobsById).filter(job => job.status === 'failed');
                        const unanalyzedCount = failedJobs.filter(job => !hasAnalysis(job.id)).length;
                        const staleCount = failedJobs.filter(job => hasAnalysis(job.id) && isAnalysisStale(job.id)).length;
                        
                        if (failedJobs.length === 0) return '';
                        if (unanalyzedCount > 0 && staleCount > 0) {
                          return ` (${unanalyzedCount + staleCount})`;
                        }
                        if (unanalyzedCount > 0) return ` (${unanalyzedCount})`;
                        if (staleCount > 0) return ` (${staleCount} stale)`;
                        return ` (${failedJobs.length})`;
                      })()}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          
          {/* Enhanced MCP Status Grid */}
          <div className="mt-3">
            <McpStatusPanel className="bg-transparent border-0 p-0" showDetails={false} />
          </div>
        </div>
      ) : (
        // Graceful degradation when MCP unavailable
        <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-yellow-50 dark:from-gray-900/20 dark:to-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/40 rounded-full flex items-center justify-center">
                <span className="text-lg">⚠️</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-yellow-900 dark:text-yellow-200">Enhanced Analysis Unavailable</h3>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">MCP orchestration services are currently offline</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-yellow-600 dark:text-yellow-400">
                  <span className="font-medium">{metrics.failed || 0}</span> Failed Jobs
                </div>
                <div className="text-xs text-yellow-500 dark:text-yellow-400">
                  Basic monitoring active
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsParaLexChatOpen(true)}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                >
                  💬 Chat with ParaLex
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Degraded Mode</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🚀 NEW: Live Agent Activity Panel */}
      {agentActivity.length > 0 && (
        <div className="mb-6 p-4 bg-lex-accent-soft rounded-lg border border-lex-accent">
          <h3 className="text-lg font-medium text-lex-accent mb-3">🤖 Live Agent Activity</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {agentActivity.map((agent) => (
              <div key={agent.agentName} className="flex items-center gap-3 p-2 bg-white dark:bg-gray-800 rounded">
                <div className={`w-3 h-3 rounded-full ${
                  agent.status === 'active' ? 'bg-green-500 animate-pulse' :
                  agent.status === 'failed' ? 'bg-red-500' : 'bg-gray-400'
                }`}></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {agent.agentName.replace(/Agent$/, '')}
                  </div>
                  <div className="text-xs text-gray-500">
                    {agent.status === 'active' && agent.currentJobId && (
                      <span>Processing {agent.currentJobId.slice(0, 8)}...</span>
                    )}
                    {agent.status === 'idle' && <span>Idle</span>}
                    {agent.status === 'failed' && <span>Failed</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Metrics Panel with MCP Cost Tracking */}
      <JobMetricsPanel 
        metrics={metrics}
        documentMetrics={documentMetrics}
        lastUpdated={lastUpdated}
        showDocumentMetrics={filter === 'document' || filter === 'all'}
        // 🚀 NEW: MCP cost tracking integration (Phase 3)
        mcpCostMetrics={(() => {
          if (!mcpHealthy) return null;
          
          const analysisCount = getAnalysisCount();
          if (analysisCount === 0) return null;
          
          // Calculate cost metrics from job analysis data
          const failedJobs = Object.values(jobsById).filter(job => job.status === 'failed');
          const analyzedJobs = failedJobs.filter(job => hasAnalysis(job.id));
          
          // Simulate cost tracking until backend provides actual data
          const totalCost = analyzedJobs.length * 0.025; // $0.025 per analysis
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const costToday = analyzedJobs.filter(job => {
            const analysisTime = getLastAnalyzedAt(job.id);
            return analysisTime && new Date(analysisTime) >= today;
          }).length * 0.025;
          
          const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const costThisMonth = analyzedJobs.filter(job => {
            const analysisTime = getLastAnalyzedAt(job.id);
            return analysisTime && new Date(analysisTime) >= firstOfMonth;
          }).length * 0.025;
          
          const mostExpensiveAnalysis = analyzedJobs.length > 0 ? {
            jobId: analyzedJobs[0].id,
            cost: 0.05, // Simulated high cost
            timestamp: getLastAnalyzedAt(analyzedJobs[0].id) || new Date().toISOString()
          } : undefined;
          
          return {
            totalAnalysisCost: totalCost,
            analysesPerformed: analysisCount,
            avgAnalysisCost: analysisCount > 0 ? totalCost / analysisCount : 0,
            costToday,
            costThisMonth,
            mostExpensiveAnalysis
          };
        })()}
        showMcpCosts={mcpHealthy && getAnalysisCount() > 0}
      />

      {/* 🚀 NEW: Live Events Section */}
      <div className="mb-6 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow">
        <div className="h-96">
          <LiveEventsViewer maxEvents={50} />
        </div>
      </div>

      {/* Enhanced Filter Buttons with MCP Analysis Counts */}
      <div className="flex flex-wrap gap-3 mb-6">
        {filterOptions.map(option => {
          const count = filterCounts[option.value];
          return (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors relative ${
                filter === option.value
                  ? 'bg-lex-accent text-lex-accent-contrast shadow-md'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <span>{option.icon}</span>
              <span>{option.label}</span>
              {count > 0 && (
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                  filter === option.value
                    ? 'bg-white/20 text-white'
                    : 'bg-lex-accent-soft text-lex-accent'
                }`}>
                  {count}
                </span>
              )}
              {/* 🚀 NEW: Stale analysis indicator for AI Analyzed filter (Phase 2) */}
              {option.value === 'ai-analyzed' && filterCounts.staleAnalysis > 0 && (
                <span 
                  className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300"
                  title={`${filterCounts.staleAnalysis} stale analyses (>15 min old)`}
                >
                  {filterCounts.staleAnalysis}⚠️
                </span>
              )}
              {option.value === 'ai-analyzed' && mcpState.isLoading && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Refresh Controls */}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} found
          {adminMode && (
            <span className="ml-2 px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-200 rounded text-xs">
              {selectedRealmId ? `Realm: ${selectedRealmId}` : `${availableRealms.length} Realms Available`}
            </span>
          )}
          {filter === 'document' && (
            <span className="ml-2 px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded text-xs">
              Document Processing
            </span>
          )}
          {recentEvents.length > 0 && (
            <span className="ml-2 px-2 py-1 bg-lex-accent-soft text-lex-accent rounded text-xs">
              {recentEvents.length} live events
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="px-3 py-1 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded text-sm transition-colors"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      {filteredJobs.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-lg mb-2">
            {filter === 'document' ? '📄' : '📋'}
          </div>
          <p className="text-gray-500 italic dark:text-gray-400">
            No jobs found for filter: <strong>{filterOptions.find(f => f.value === filter)?.label}</strong>
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl shadow border dark:border-gray-700">
          <table className="min-w-full text-sm text-left text-gray-900 dark:text-gray-100">
            <thead className="bg-gray-100 dark:bg-gray-850">
              <tr>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Job ID</th>
                <th className="px-4 py-3">Name</th>
                {adminMode && <th className="px-4 py-3">Realm</th>}
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3">Attempts</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map(job => {
                // 🚀 OPTIMIZED: Cache MCP analysis data to avoid redundant calls (Phase 2)
                const mcpIcon = getMcpAnalysisIndicator(job.id);
                const analysisStatus = getAnalysisStatus(job.id);
                const analysisSummary = getAnalysisSummary(job.id);
                const lastAnalyzed = getLastAnalyzedAt(job.id);
                const isStale = isAnalysisStale(job.id);
                
                // Generate tooltip content
                const getAnalysisTooltip = () => {
                  if (!mcpIcon) return '';
                  
                  const statusText = analysisStatus.charAt(0).toUpperCase() + analysisStatus.slice(1);
                  const freshness = lastAnalyzed 
                    ? `Analyzed ${new Date(lastAnalyzed).toLocaleString()}`
                    : 'Recently analyzed';
                  const staleWarning = isStale ? ' (May be stale)' : '';
                  
                  return `AI Analysis: ${statusText} - ${freshness}${staleWarning}`;
                };
                
                return (
                  <tr
                    key={job.id}
                    className="border-t border-gray-200 dark:border-gray-750 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-3 text-center">
                      <span className="text-lg" title={isDocumentJob(job.name) ? 'Document Job' : 'General Job'}>
                        {getJobIcon(job.name)}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{job.id}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{job.name}</span>
                          {/* 🚀 ENHANCED: MCP Analysis Indicators with tooltips (Phase 2) */}
                          {mcpIcon && (
                            <span 
                              className={`text-sm cursor-help ${isStale ? 'opacity-60' : ''}`}
                              title={getAnalysisTooltip()}
                            >
                              {mcpIcon}
                            </span>
                          )}
                        </div>
                      {job.data?._meta?.traceId && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Trace: {job.data._meta.traceId.slice(0, 8)}...
                        </span>
                      )}
                      {job.progressPhase && (
                        <span className="text-xs text-lex-accent">
                          {job.progressPhase}
                        </span>
                      )}
                      {/* 🚀 ENHANCED: MCP Analysis Summary with rich display (Phase 2) */}
                      {job.status === 'failed' && hasAnalysis(job.id) && (
                        <div className={`text-xs mt-1 ${isStale ? 'opacity-60' : ''}`}>
                          <span className="flex items-center gap-1">
                            <span className={`
                              ${analysisStatus === 'success' ? 'text-green-600 dark:text-green-400' : ''}
                              ${analysisStatus === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : ''}
                              ${analysisStatus === 'error' ? 'text-red-600 dark:text-red-400' : ''}
                              ${analysisStatus === 'unknown' ? 'text-purple-600 dark:text-purple-400' : ''}
                            `}>
                              {mcpIcon}
                            </span>
                            <span className="truncate max-w-xs" title={analysisSummary || 'Analysis available'}>
                              {analysisSummary?.split('.')[0] || 'Analysis available'}
                              {isStale && <span className="ml-1 text-gray-500">(stale)</span>}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  </td>
                  {adminMode && (
                    <td className="px-4 py-3">
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                        {job.data?._meta?.realmId || job.data?.realmId || 'unknown'}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={getStatusBadge(job.status)}>
                        {job.status}
                      </span>
                      {/* 🚀 ENHANCED: MCP Analysis Badge with status-aware styling (Phase 2) */}
                      {hasAnalysis(job.id) && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                          analysisStatus === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                          analysisStatus === 'warning' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          analysisStatus === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        } ${isStale ? 'opacity-70' : ''}`}
                        title={getAnalysisTooltip()}>
                          {mcpIcon} AI Analyzed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {job.status === 'active' && typeof job.progress === 'number' ? (
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-lex-accent h-2 rounded-full transition-all duration-300" 
                            style={{ width: `${job.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{job.progress}%</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{job.attempts}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        className="text-lex-accent hover:text-lex-accent-hover font-medium transition-colors"
                        onClick={async () => {
                          // 🚀 NEW: Enhanced with MCP job details if available
                          if (mcpHealthy && hasAnalysis(job.id)) {
                            try {
                              // Refresh analysis data to get latest results
                              await refreshAnalysisData([job.id]);
                              pushNotification(`Updated analysis data for job ${job.id}`, 'success');
                            } catch (error) {
                              console.warn('Failed to refresh MCP job details:', error);
                              // Continue to open modal even if MCP refresh fails
                            }
                          }
                          openJobModal(job.id);
                        }}
                      >
                        View
                      </button>
                      <Link
                        to={`/system-monitor/job-trace/${job.id}`}
                        className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 font-medium transition-colors"
                      >
                        Trace
                      </Link>
                      {/* 🚀 NEW: MCP Analysis Action for failed jobs */}
                      {job.status === 'failed' && mcpHealthy && !hasAnalysis(job.id) && (
                        <button
                          className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={async () => {
                            try {
                              await analyzeFailedJob(job.id);
                              pushNotification(`Analysis completed for job ${job.id}`, 'success');
                            } catch (error) {
                              console.error('Failed to analyze job:', error);
                              pushNotification(`Failed to analyze job: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error', false);
                            }
                          }}
                          disabled={isAnalyzing}
                        >
                          {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Enhanced Job Detail Modal */}
      <JobDetailModal
        isOpen={!!selectedJob}
        onClose={closeJobModal}
        job={selectedJob}
      />

      {/* ParaLex System Chat */}
      <ParaLexTraceChat
        isOpen={isParaLexChatOpen}
        onClose={() => setIsParaLexChatOpen(false)}
      />
    </div>
  );
}
